<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { computed, nextTick, onMounted, onUnmounted, watch } from "vue";
  import ConfirmDialog from "./shared/confirm-dialog.vue";
  import LotCodeCombobox from "./lot-code-combobox.vue";
  import WrapWithHint from "./wrap-with-hint.vue";
  import { displayError, vFieldErrors } from "~/jose_modern/field_with_errors";
  import { quantityLessOnShipmentWithUnit } from "~/jose_modern/quantity_with_unit";
  import { useLineItemRow } from "./line-items/use-line-item-row.js";

  const props = defineProps({
    item: { type: Object, default: null },
    itemCombobox: { type: [Object, Function], required: true },
    comboboxProps: { type: Object, default: () => ({}) },
    mapSelection: { type: Function, required: true },
    api: { type: Object, required: true },
    labels: { type: Object, default: () => ({}) },
    excludedItemIds: { type: Array, default: () => [] },
    duplicate: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    lotSeeds: { type: Object, default: () => ({}) },
    lotCodesUrl: { type: String, default: "" },
    requireLotCode: { type: Boolean, default: false },
    shippedLotIds: { type: Array, default: () => [] },
    groupFirst: { type: Boolean, default: true },
    groupLast: { type: Boolean, default: true },
    groupItems: { type: Array, default: () => [] },
    removeSiblings: { type: Function, default: () => Promise.resolve(true) },
  });

  const emit = defineEmits(["created", "remove", "dirty-changed", "item-changed", "insert-row"]);

  const REQUIRED_LOT_HINT =
    "Your account requires lot codes, so this line will not save until you pick one.";

  const DUPLICATE_LOT_HINT =
    "Another line already ships this product from this lot. Pick a different lot to save this one.";

  const itemSearchRef = _ref(null);
  const quantityInput = _ref(null);
  const lotField = _ref(null);
  let duplicateTimer = null;

  function hideDuplicateError() {
    clearTimeout(duplicateTimer);
    lotField.value?._tippy?.destroy();
  }

  watch(
    () => props.duplicate,
    (duplicate) => {
      hideDuplicateError();
      // The click that picks the offending lot would dismiss a tooltip opened
      // within it, so the error lands just after the selection settles.
      if (duplicate)
        duplicateTimer = setTimeout(() => displayError(lotField.value, DUPLICATE_LOT_HINT), 50);
    }
  );

  onUnmounted(hideDuplicateError);

  function round(value) {
    return Number(value.toFixed(6));
  }

  function emptyLot() {
    return {
      id: null,
      lot_code: "",
      remaining_in_unit: null,
      on_shipment_in_unit: 0,
      remaining: null,
    };
  }

  function remainingFields(source) {
    const quantity = source.remaining_quantity == null ? null : Number(source.remaining_quantity);
    const onShipment = Number(source.on_shipment_quantity ?? 0);
    return {
      remaining_in_unit:
        source.remaining_quantity_units == null ? null : Number(source.remaining_quantity_units),
      on_shipment_in_unit: Number(source.on_shipment_quantity_units ?? 0),
      remaining:
        quantity == null
          ? null
          : quantityLessOnShipmentWithUnit(
              round(quantity),
              round(onShipment),
              source.remaining_unit
            ),
    };
  }

  const selectedLotCode = props.item?.persisted_lot_code;
  let lot = _ref(
    selectedLotCode
      ? {
          id: selectedLotCode.id,
          lot_code: selectedLotCode.lot_code,
          ...remainingFields(selectedLotCode),
        }
      : emptyLot()
  );

  const {
    labels,
    itemId,
    name,
    itemCustomName,
    displayName,
    quantity,
    unit,
    units,
    saving,
    errored,
    showConfirmRemove,
    rowEl,
    isDraft,
    quantityError,
    excludedItemIds,
    selectItem,
    clearSelection,
    onRowFocusOut,
    onEnter,
    onFieldChange,
    removeItem,
    confirmRemove,
  } = useLineItemRow(props, emit, {
    attributes: (base) => ({ ...base, persisted_lot_code_id: lot.value.id }),
    identity: (base) => ({ ...base, persisted_lot_code_id: lot.value.id }),
    canSave: () => (!props.requireLotCode || lot.value.id != null) && !props.duplicate,
    focusItem: () => itemSearchRef.value?.focus(),
    focusQuantity: () => quantityInput.value?.focus(),
  });

  const EMPTY_SEED = { seed: [], total: 0 };
  const seededItemId = props.item?.id == null ? null : props.item.item_id;
  const seededUnit = props.item?.unit ?? null;

  const isSeeded = _computed(
    () => itemId.value != null && itemId.value === seededItemId && unit.value === seededUnit
  );
  const lotSeed = _computed(() =>
    isSeeded.value ? (props.lotSeeds[props.item?.id] ?? EMPTY_SEED) : EMPTY_SEED
  );

  const searchLotsRemotely = _computed(() => !isSeeded.value || lotSeed.value.total > lotSeed.value.seed.length);

  const lotSearchParams = _computed(() => ({ item_id: itemId.value, unit: unit.value }));

  const lotItems = _computed(() =>
    lot.value.id == null
      ? []
      : [
          {
            value: lot.value.id,
            persisted_lot_code_id: lot.value.id,
            lot_code: lot.value.lot_code,
            label: lot.value.lot_code,
            remaining_in_unit: lot.value.remaining_in_unit,
            on_shipment_in_unit: lot.value.on_shipment_in_unit,
            remaining: lot.value.remaining,
          },
        ]
  );

  const otherShippedLotIds = _computed(() => props.shippedLotIds.filter((id) => id !== lot.value.id));

  watch(unit, () => {
    lot.value.remaining_in_unit = null;
    lot.value.on_shipment_in_unit = 0;
    lot.value.remaining = null;
  });

  const availableInUnit = computed(() =>
    lot.value.remaining_in_unit == null ? null : round(lot.value.remaining_in_unit - lot.value.on_shipment_in_unit)
  );

  const isOverdrawn = computed(() => {
    if (availableInUnit.value == null) return false;

    return round((Number(quantity.value) || 0) - availableInUnit.value) > 0;
  });

  const missingQuantity = computed(() => itemId.value != null && quantity.value === "");

  const missingRequiredLot = computed(
    () =>
      props.requireLotCode && lot.value.id == null && itemId.value != null && Number(quantity.value) > 0
  );

  const overdrawHint = computed(() => {
    const inRowUnit =
      lot.value.remaining_in_unit == null
        ? null
        : quantityLessOnShipmentWithUnit(
            round(lot.value.remaining_in_unit),
            round(lot.value.on_shipment_in_unit),
            unit.value
          );
    const converted = inRowUnit && inRowUnit !== lot.value.remaining ? ` (${inRowUnit})` : "";
    const onShipment = lot.value.on_shipment_in_unit
      ? " The amount in parentheses is already on other active shipments."
      : "";

    return (
      `This ships more than the ${lot.value.remaining ?? "stock"}${converted} left in this lot.${onShipment} ` +
      "Shipping it will leave the lot negative."
    );
  });

  function saveLotChange() {
    nextTick(onFieldChange);
  }

  function selectLot(option) {
    lot.value = {
      id: option.persisted_lot_code_id,
      lot_code: option.lot_code,
      remaining_in_unit: option.remaining_in_unit ?? null,
      on_shipment_in_unit: option.on_shipment_in_unit ?? 0,
      remaining: option.remaining ?? null,
    };
    saveLotChange();
  }

  function clearLot() {
    lot.value = emptyLot();
  }

  function onLotCleared() {
    clearLot();
    saveLotChange();
  }

  function selectFinishedGood(result) {
    clearLot();
    selectItem(result);
  }

  function clearFinishedGood() {
    clearLot();
    clearSelection({ keepQuantity: true });
  }

  let showConfirmChangeProduct = _ref(false);
  const otherLotCount = _computed(() => Math.max(props.groupItems.length - 1, 0));

  function changeProduct() {
    if (otherLotCount.value > 0) showConfirmChangeProduct.value = true;
    else clearFinishedGood();
  }

  async function confirmChangeProduct() {
    saving.value = true;
    try {
      if (!(await props.removeSiblings())) return;

      showConfirmChangeProduct.value = false;
      clearFinishedGood();
    } finally {
      saving.value = false;
    }
  }

  function addLot() {
    emit("insert-row", {
      item_id: itemId.value,
      item_name: name.value,
      item_custom_name: itemCustomName.value,
      unit: unit.value,
      units: units.value,
    });
  }

  onMounted(() => {
    if (props.autofocus && itemId.value) nextTick(() => lotField.value?.querySelector("input")?.focus());
  });
</script>

<template>
  <div
    ref="rowEl"
    class="line-items-row shipment-item-row"
    :class="{
      'draft': isDraft,
      'errored': errored || duplicate,
      'group-first': groupFirst,
      'group-last': groupLast,
      'group-closes': groupLast && groupItems.length < 2,
      'grouped': groupItems.length > 1,
    }"
    @focusout="onRowFocusOut"
    @keydown.enter="onEnter"
  >
    <div class="item-cell is-relative" :class="{ empty: !groupFirst }">
      <div v-if="groupFirst" class="field">
        <label class="label hidden-desktop">Item</label>
        <div v-if="itemId" class="field has-addons mb-0">
          <div class="control is-expanded">
            <input class="input" type="text" :value="displayName" disabled />
          </div>
          <div class="control">
            <button
              class="button change-product-btn"
              :class="{ 'is-loading': saving }"
              :disabled="saving"
              title="Change product"
              @click="changeProduct"
            >
              <span class="icon is-small"><i class="fas fa-edit"></i></span>
            </button>
          </div>
        </div>
        <component
          :is="itemCombobox"
          v-else
          ref="itemSearchRef"
          v-bind="comboboxProps"
          :excluded-item-ids="excludedItemIds"
          :autofocus="autofocus"
          @select="selectFinishedGood"
        />
      </div>
    </div>
    <div class="qty-cell">
      <div class="field qty mb-0">
        <label v-if="groupFirst" class="label hidden-desktop">Quantity</label>
        <div class="field has-addons">
          <div class="control">
            <input
              ref="quantityInput"
              v-model="quantity"
              v-field-errors
              class="input"
              :class="{ 'is-danger': quantityError, 'is-warning': missingQuantity }"
              :disabled="saving"
              type="number"
              placeholder="Qty"
              :data-errors="
                quantityError ? JSON.stringify({ quantity: [quantityError] }) : undefined
              "
            />
          </div>
          <div class="control">
            <div class="select unit">
              <select v-model="unit" :disabled="saving || !units.length">
                <option v-if="!units.length" value="" disabled>Unit</option>
                <option v-for="u in units" :key="u" :value="u">{{ u }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="lot-cell">
      <div class="field mb-0">
        <label v-if="groupFirst" class="label hidden-desktop">Lot code</label>
        <div ref="lotField">
          <LotCodeCombobox
            :key="`${itemId ?? 'no-item'}:${unit}`"
            :model-value="lot.id ?? ''"
            :items="lotItems"
            :source-url="lotCodesUrl"
            :search-params="lotSearchParams"
            :search-remotely="searchLotsRemotely"
            :seed="lotSeed.seed"
            :seed-total="lotSeed.total"
            :unit="unit"
            :disabled="saving || !itemId"
            :has-error="duplicate"
            :allow-create="false"
            :in-use-values="otherShippedLotIds"
            in-use-label="In shipment"
            :is-required="missingRequiredLot"
            :required-hint="REQUIRED_LOT_HINT"
            :is-duplicate="duplicate"
            :duplicate-hint="DUPLICATE_LOT_HINT"
            :is-negative="isOverdrawn"
            :negative-hint="overdrawHint"
            placeholder="Lot code"
            @select="selectLot"
            @clear="onLotCleared"
          />
        </div>
      </div>
    </div>
    <div class="row-actions">
      <WrapWithHint v-if="itemId" tag="span" content="Add another lot of this product">
        <button class="button is-ghost add-lot-btn" :disabled="saving" @click="addLot">
          <span class="icon is-small"><i class="fas fa-plus"></i></span>
        </button>
      </WrapWithHint>
      <button class="button is-ghost remove-btn is-danger" :disabled="saving" @click="removeItem">
        <span class="icon is-small"><i class="fas fa-trash-can"></i></span>
      </button>
    </div>
  </div>

  <ConfirmDialog
    v-model="showConfirmRemove"
    :title="labels.removeTitle"
    message=""
    confirm-text="Remove"
    :is-loading="saving"
    @confirm="confirmRemove"
  >
    <p>
      {{ labels.removeMessage }}<br />
      <b>{{ name }}</b>
    </p>
  </ConfirmDialog>

  <ConfirmDialog
    v-model="showConfirmChangeProduct"
    title="Change Product"
    message=""
    confirm-text="Change Product"
    :is-loading="saving"
    @confirm="confirmChangeProduct"
  >
    <p>
      Lot codes belong to a product, so changing it clears every lot of <b>{{ name }}</b
      >. The other {{ otherLotCount }} lot {{ otherLotCount === 1 ? "line" : "lines" }} will be
      removed and only this line's quantity is kept.
    </p>
  </ConfirmDialog>
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";
  @import "./line-items/line-items";

  .shipment-item-row {
    @include line-items-columns;

    position: relative;
    border-bottom: 1px solid $border-light;

    &:not(.group-closes) {
      border-bottom: none;
    }

    @media screen and (min-width: $line-items-collapse + 1) {
      &.group-closes {
        border-bottom-color: $border;
        margin-bottom: 0.5rem;
      }

      &.grouped::before {
        content: "";
        position: absolute;
        left: 1.25rem;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: $border;
      }

      &.grouped.group-first::before {
        top: calc(50% + 1.25rem);
      }

      &.grouped.draft,
      &.grouped.errored {
        border-left: none;
      }

      &.grouped.draft::before {
        background-color: $secondary;
      }

      &.grouped.errored::before {
        background-color: $danger;
      }

      .label {
        display: none;
      }
    }

    @media screen and (max-width: $line-items-collapse) {
      @include line-items-card-sides;

      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: 0.5rem 0.75rem;
      padding: 0.25rem 1rem;
      border-bottom: none;

      &.group-first {
        @include line-items-card-top;
      }

      &.group-closes {
        @include line-items-card-bottom;
      }

      &.grouped.group-last {
        padding-bottom: 0.75rem;
      }

      .item-cell {
        flex: 1 1 100%;

        &.empty {
          display: none;
        }
      }

      .qty-cell,
      .lot-cell {
        flex: 1 1 12rem;
      }

      .row-actions {
        flex: 0 0 auto;
        margin-left: auto;
      }
    }
  }

  .draft {
    border-left: 3px solid $secondary;
  }

  .errored {
    border-left: 3px solid $danger;
  }

  .qty {
    .input {
      width: 12ch;
    }
    .select {
      max-width: 20ch;
    }
  }

  .row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;

    .button {
      align-self: end;
    }
  }

  .row-actions .button:not([disabled]) {
    opacity: 0;
    transition: opacity 0.15s;
  }

  .shipment-item-row:hover .row-actions .button,
  .row-actions .button:focus {
    &:not([disabled]) {
      opacity: 1;
    }
  }

  .unit select {
    width: 20ch;
  }

  @include touch {
    .row-actions .button:not([disabled]) {
      opacity: 1;
    }

    .qty .select,
    .unit select {
      max-width: 100%;
      width: 100%;
    }
  }
</style>
